import { supabase } from './supabaseStorage';

export interface NotificationPayload {
  hy_user_id: string;
  title: string;
  body: string;
  data?: Record<string, any>;
  image_url?: string;
  action_url?: string;
  priority?: 'normal' | 'high';
  badge?: number;
}

export interface NotificationResult {
  success: boolean;
  message_id?: string;
  error?: string;
  sent_to?: string;
  timestamp?: string;
}

/**
 * Send push notification to a user via Firebase Cloud Messaging
 */
export const sendNotificationToUser = async (
  payload: NotificationPayload
): Promise<NotificationResult> => {
  try {
    console.log('🔔 [NOTIFICATION] Sending notification to user:', payload.hy_user_id);
    console.log('📋 [NOTIFICATION] Payload:', {
      title: payload.title,
      body: payload.body,
      has_image: !!payload.image_url,
      has_action: !!payload.action_url,
      priority: payload.priority
    });

    // Call the Firebase notification Edge Function
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/firebase-notify`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const result = await response.json();

    if (!response.ok) {
      console.error('❌ [NOTIFICATION] Failed to send notification:', result);
      return {
        success: false,
        error: result.error || 'Failed to send notification'
      };
    }

    console.log('✅ [NOTIFICATION] Notification sent successfully:', result);
    return result;

  } catch (error) {
    console.error('💥 [NOTIFICATION] Error sending notification:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Send notification when pro request is completed
 */
export const notifyProRequestCompleted = async (
  hy_user_id: string,
  requestType: string,
  assetUrl?: string
): Promise<NotificationResult> => {
  return sendNotificationToUser({
    hy_user_id,
    title: '🎉 Your Pro Request is Ready!',
    body: `Your ${requestType.replace(/_/g, ' ')} request has been completed by our team.`,
    data: {
      type: 'pro_request_completed',
      request_type: requestType,
      asset_url: assetUrl
    },
    image_url: assetUrl,
    action_url: '/assets',
    priority: 'high',
    badge: 1
  });
};

/**
 * Send notification when AI generation is completed
 */
export const notifyGenerationCompleted = async (
  hy_user_id: string,
  modelName: string,
  assetCount: number,
  firstAssetUrl?: string
): Promise<NotificationResult> => {
  return sendNotificationToUser({
    hy_user_id,
    title: '✨ AI Generation Complete!',
    body: `Your ${modelName} generated ${assetCount} new asset${assetCount !== 1 ? 's' : ''}.`,
    data: {
      type: 'generation_completed',
      model_name: modelName,
      asset_count: assetCount,
      first_asset_url: firstAssetUrl
    },
    image_url: firstAssetUrl,
    action_url: '/assets',
    priority: 'normal',
    badge: assetCount
  });
};

/**
 * Send notification when credits are low
 */
export const notifyLowCredits = async (
  hy_user_id: string,
  currentCredits: number,
  threshold: number = 10
): Promise<NotificationResult> => {
  return sendNotificationToUser({
    hy_user_id,
    title: '⚠️ Low Credits Warning',
    body: `You have ${currentCredits} credits remaining. Consider purchasing more to continue using AI features.`,
    data: {
      type: 'low_credits',
      current_credits: currentCredits,
      threshold: threshold
    },
    action_url: '/upgrade',
    priority: 'normal'
  });
};

/**
 * Send welcome notification to new users
 */
export const notifyWelcomeUser = async (
  hy_user_id: string,
  userName: string
): Promise<NotificationResult> => {
  return sendNotificationToUser({
    hy_user_id,
    title: `Welcome to Hyper ProMax, ${userName}! 🚀`,
    body: 'Start creating amazing content with our AI-powered tools. Tap to explore!',
    data: {
      type: 'welcome',
      user_name: userName
    },
    action_url: '/',
    priority: 'normal'
  });
};

/**
 * Send notification when subscription expires soon
 */
export const notifySubscriptionExpiring = async (
  hy_user_id: string,
  daysLeft: number,
  planName: string
): Promise<NotificationResult> => {
  return sendNotificationToUser({
    hy_user_id,
    title: '⏰ Subscription Expiring Soon',
    body: `Your ${planName} expires in ${daysLeft} day${daysLeft !== 1 ? 's' : ''}. Renew now to keep your access.`,
    data: {
      type: 'subscription_expiring',
      days_left: daysLeft,
      plan_name: planName
    },
    action_url: '/upgrade',
    priority: 'high'
  });
};

/**
 * Register user's FCM token for notifications
 */
export const registerFCMToken = async (
  hy_user_id: string,
  fcmToken: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📱 [FCM-REGISTER] Registering FCM token for user:', hy_user_id);

    const { error } = await supabase
      .from('users')
      .update({ fcm_token: fcmToken })
      .eq('hy_user_id', hy_user_id);

    if (error) {
      console.error('❌ [FCM-REGISTER] Failed to register token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [FCM-REGISTER] FCM token registered successfully');
    return { success: true };

  } catch (error) {
    console.error('💥 [FCM-REGISTER] Error registering FCM token:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};

/**
 * Unregister user's FCM token
 */
export const unregisterFCMToken = async (
  hy_user_id: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    console.log('📱 [FCM-UNREGISTER] Unregistering FCM token for user:', hy_user_id);

    const { error } = await supabase
      .from('users')
      .update({ fcm_token: null })
      .eq('hy_user_id', hy_user_id);

    if (error) {
      console.error('❌ [FCM-UNREGISTER] Failed to unregister token:', error);
      return { success: false, error: error.message };
    }

    console.log('✅ [FCM-UNREGISTER] FCM token unregistered successfully');
    return { success: true };

  } catch (error) {
    console.error('💥 [FCM-UNREGISTER] Error unregistering FCM token:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Unknown error' 
    };
  }
};